/**
 * Sticky quick-links bar (FR-COACH-07).
 *
 * The five game screens a coach jumps between mid-drive — Play Entry, Game
 * Summary, Play Archive, Stat Tracker, Playbook — pinned to the bottom of the
 * viewport where a thumb can reach them. Every destination is one tap away
 * from every other, which is the whole point: no menu, no back button.
 *
 * Every link meets the 44 px WCAG target floor at every breakpoint.
 */
import { Link, useLocation } from 'react-router-dom';

import { useSession } from '../app/session-context';
import { APP_VIEWS } from '../lib/constants';

export interface QuickLink {
  /** View slug under `/app/{tenant_slug}`. */
  view: string;
  label: string;
  /** Used below 400px, where five full labels no longer fit. */
  shortLabel: string;
  icon: string;
}

/** FR-COACH-07 fixes both the membership and the order of this list. */
export const QUICK_LINKS: readonly QuickLink[] = [
  { view: APP_VIEWS.playEntry, label: 'Play Entry', shortLabel: 'Entry', icon: '✎' },
  { view: APP_VIEWS.gameSummary, label: 'Game Summary', shortLabel: 'Summary', icon: '▤' },
  { view: APP_VIEWS.playArchive, label: 'Play Archive', shortLabel: 'Archive', icon: '≡' },
  { view: APP_VIEWS.statTracker, label: 'Stat Tracker', shortLabel: 'Stats', icon: '∑' },
  { view: APP_VIEWS.playbook, label: 'Playbook', shortLabel: 'Playbook', icon: '⛨' },
];

export interface QuickLinksBarProps {
  /**
   * The view slug the user is already on. It stays in the bar (so the five
   * slots never move between screens) but is marked `aria-current`.
   */
  currentView?: string;
  /** Overrides the session's slug; only tests and previews need this. */
  tenantSlug?: string | null;
  /** Extra query string appended to every destination, e.g. `?event_id=…`. */
  search?: string;
}

export function QuickLinksBar({
  currentView,
  tenantSlug,
  search = '',
}: QuickLinksBarProps): JSX.Element {
  const session = useSession();
  const { pathname } = useLocation();
  const slug = tenantSlug ?? session.tenantSlug;

  return (
    <nav className="quick-links" aria-label="Game screens" data-testid="quick-links-bar">
      <ul className="quick-links__list">
        {QUICK_LINKS.map((link) => {
          const to = slug === null ? '#' : `/app/${slug}/${link.view}${search}`;
          const isCurrent =
            currentView === link.view || pathname === `/app/${slug ?? ''}/${link.view}`;
          return (
            <li key={link.view} className="quick-links__item">
              <Link
                to={to}
                className="quick-links__link"
                aria-current={isCurrent ? 'page' : undefined}
                aria-disabled={slug === null ? true : undefined}
              >
                <span className="quick-links__icon" aria-hidden="true">
                  {link.icon}
                </span>
                <span className="quick-links__label">{link.label}</span>
                <span className="quick-links__label quick-links__label--short" aria-hidden="true">
                  {link.shortLabel}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
